import { useEffect, useRef, useState } from 'react'

export interface IViewObserveConfig<T> {
  getContainer: () => T | undefined | null
  watchVisible?: boolean
}

function useViewObserve<T extends HTMLElement = HTMLDivElement>(
  callback: () => void,
  { getContainer, watchVisible = true }: IViewObserveConfig<T>
) {
  const [container, setContainer] = useState<T | undefined | null>()

  useEffect(() => {
    setContainer(getContainer())
  }, [getContainer])

  const isIntersectingRef = useRef(false)

  const callbackRef = useRef<() => void>()

  useEffect(() => {
    callbackRef.current = () => {
      if (isIntersectingRef.current) {
        callback()
      }
    }
  }, [callback])

  // 修改内容
  const mutationObserverRef = useRef<MutationObserver>()
  useEffect(() => {
    mutationObserverRef.current = new MutationObserver((mutations) => {
      if (
        mutations.every(
          (d) => d.target === container && d.type === 'attributes'
        )
      ) {
        return
      }
      callbackRef.current!()
    })
  }, [])
  useEffect(() => {
    if (!container) {
      return
    }
    const observe = mutationObserverRef.current!
    observe.observe(container, {
      attributes: true,
      childList: true,
      subtree: true,
      characterData: true,
    })
    return () => {
      observe.disconnect()
    }
  }, [container])

  // 缩放屏幕
  useEffect(() => {
    const handleResize = () => {
      callbackRef.current!()
    }
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  // 显示隐藏
  const intersectionObserverRef = useRef<IntersectionObserver>()
  useEffect(() => {
    if (!watchVisible) {
      return
    }
    intersectionObserverRef.current = new IntersectionObserver((changes) => {
      changes.forEach((change) => {
        if (change.isIntersecting) {
          isIntersectingRef.current = true
          callbackRef.current!()
        }
      })
    })
  }, [watchVisible])
  useEffect(() => {
    if (!container || !watchVisible) {
      return
    }
    const observe = intersectionObserverRef.current!
    observe.observe(container)
    return () => {
      observe.disconnect()
    }
  }, [container, watchVisible])
}

export default useViewObserve
